/**
 * Reseller-domain seam: the active reseller and its customers / staff / revenue
 * / KPIs. Reseller-shell pages read through these hooks instead of importing the
 * mock consts directly, so the mock→real flip is an env change (VITE_USE_MOCKS).
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGet, apiGetList, apiPost, apiPatch, apiDelete } from './client';
import {
  currentReseller, resellerCustomers, resellerStaff, resellerRevenue, resellerKpis,
  currentApplicantContext,
} from '../mocks/reseller';
import {
  resellerPayments, resellerPackages, platformPricingTiers,
  type ResellerPayment, type ResellerPackage, type PlatformPricingTier,
} from '../mocks/admin';

export const getCurrentReseller = () =>
  USE_MOCKS ? mockDelay(currentReseller) : apiGet<typeof currentReseller>('/reseller/me');
/** Reseller self-service update (PATCH /reseller/me) — name/support/sender + the branding blob. */
export const updateReseller = (patch: { name?: string; ownerEmail?: string; senderDomain?: string; branding?: Record<string, unknown>; payout?: Record<string, unknown>; notifPrefs?: Record<string, boolean>; allowedDbModes?: string[] }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch<typeof currentReseller>('/reseller/me', patch);
export const listResellerCustomers = () =>
  USE_MOCKS ? mockDelay(resellerCustomers) : apiGetList<(typeof resellerCustomers)[number]>('/reseller/customers');
export const listResellerStaff = () =>
  USE_MOCKS ? mockDelay(resellerStaff) : apiGetList<(typeof resellerStaff)[number]>('/reseller/staff');
export const getResellerRevenue = () =>
  USE_MOCKS ? mockDelay(resellerRevenue) : apiGet<typeof resellerRevenue>('/reseller/revenue');
export const getResellerKpis = () =>
  USE_MOCKS ? mockDelay(resellerKpis) : apiGet<typeof resellerKpis>('/reseller/kpis');

// ── Reseller's own recent activity (ResellerOverview) — real audit_logs feed ──
export type ResellerActivityRow = {
  id: string;
  action: string;
  category: string | null;
  severity: string | null;
  actorName: string | null;
  actorEmail: string | null;
  target: string | null;
  occurredAt: string | null;
};
const MOCK_RESELLER_ACTIVITY: ResellerActivityRow[] = [
  { id: 'a1', action: 'business.created', category: 'business', severity: 'info', actorName: 'Rakib Ahmed', actorEmail: 'rakib@acme-resellers.com', target: 'business · Greenfields Mart', occurredAt: '2026-05-03T09:10:00Z' },
  { id: 'a2', action: 'payment.received', category: 'billing', severity: 'info', actorName: 'System', actorEmail: null, target: 'business · Sky Pharmacy', occurredAt: '2026-04-30T14:05:00Z' },
  { id: 'a3', action: 'subscription.past_due', category: 'billing', severity: 'warning', actorName: 'System', actorEmail: null, target: 'business · Khan Bakery', occurredAt: '2026-04-25T02:00:00Z' },
];
export const listResellerActivity = () =>
  USE_MOCKS ? mockDelay(MOCK_RESELLER_ACTIVITY) : apiGetList<ResellerActivityRow>('/reseller/activity');
export const getApplicantContext = () =>
  USE_MOCKS ? mockDelay(currentApplicantContext) : apiGet<typeof currentApplicantContext>('/reseller/application/me');

/** Body for the public reseller application (POST /reseller-applications). */
export interface ResellerApplicationBody {
  orgName: string;
  applicantName: string;
  email: string;
  phone?: string;
  country?: string;
  website?: string;
  expectedVolumeMinor?: number;
  whyUs?: string;
  referencesText?: string;
}
/** Public: submit a reseller application. Returns the created application's id + status. */
export const submitResellerApplication = (body: ResellerApplicationBody) =>
  USE_MOCKS
    ? mockDelay({ id: 'app_new', status: 'pending' })
    : apiPost<{ id: string; status: string }>('/reseller-applications', body);
// The reseller's OWN commission lines — already RLS-scoped (no resellerId filter needed).
export const listResellerPayments = (): Promise<ResellerPayment[]> =>
  USE_MOCKS ? mockDelay(resellerPayments.rsl_1 ?? []) : apiGetList<ResellerPayment>('/reseller/payments');

// ── Customer detail tabs: business-scoped domains + audit (CustomerDetail #58) ─
export type CustomerDomain = {
  id: string;
  host: string;
  isPrimary: boolean;
  dnsStatus: 'pending' | 'verified' | 'failed';
  tlsStatus: 'pending' | 'issued' | 'failed';
  verifiedAt: string | null;
  createdAt: string | null;
};
const MOCK_CUSTOMER_DOMAINS: CustomerDomain[] = [
  { id: 'cd_1', host: 'khan-bakery.itcitysoft.com', isPrimary: true, dnsStatus: 'verified', tlsStatus: 'issued', verifiedAt: '2026-01-25T09:00:00Z', createdAt: '2026-01-25T09:00:00Z' },
];
export const listCustomerDomains = (businessId: string) =>
  USE_MOCKS ? mockDelay(MOCK_CUSTOMER_DOMAINS) : apiGetList<CustomerDomain>(`/reseller/customers/${businessId}/domains`);
export const useCustomerDomains = (businessId: string) =>
  useQuery({ queryKey: ['reseller', 'customer', businessId, 'domains'], queryFn: () => listCustomerDomains(businessId), enabled: !!businessId });

export type CustomerAuditEvent = {
  id: string;
  action: string;
  category: string;
  severity: string;
  outcome: string;
  actorName: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  occurredAt: string | null;
  diff: Record<string, { from: unknown; to: unknown }> | null;
  metadata: Record<string, unknown> | null;
};
const MOCK_CUSTOMER_AUDIT: CustomerAuditEvent[] = [
  { id: 'ca_1', action: 'dunning.email.sent', category: 'billing', severity: 'info', outcome: 'success', actorName: 'Rakib Ahmed', actorEmail: 'rakib@acme-resellers.com', actorRole: 'reseller_admin', occurredAt: '2026-05-01T09:14:00Z', diff: null, metadata: null },
  { id: 'ca_2', action: 'subscription.auto_renew.failed', category: 'billing', severity: 'warning', outcome: 'failure', actorName: null, actorEmail: null, actorRole: null, occurredAt: '2026-04-25T03:00:00Z', diff: null, metadata: null },
  { id: 'ca_3', action: 'business.created', category: 'system', severity: 'info', outcome: 'success', actorName: 'Rakib Ahmed', actorEmail: 'rakib@acme-resellers.com', actorRole: 'reseller_admin', occurredAt: '2026-01-25T09:00:00Z', diff: null, metadata: null },
];
export const listCustomerAudit = (businessId: string) =>
  USE_MOCKS ? mockDelay(MOCK_CUSTOMER_AUDIT) : apiGetList<CustomerAuditEvent>(`/reseller/customers/${businessId}/audit`);
export const useCustomerAudit = (businessId: string) =>
  useQuery({ queryKey: ['reseller', 'customer', businessId, 'audit'], queryFn: () => listCustomerAudit(businessId), enabled: !!businessId });

// ── Business-DB extraction (mirrors api/admin.ts's BusinessExtraction — same wire
// shape from extraction-api.ts's serializer, ISO date strings, no verifyReport/
// mediaReport field). GET is ownership-scoped (a foreign customer 404s, same pattern
// as cancelCustomerSubscription); POST additionally 403s unless this reseller's own
// allowed_db_modes includes 'managed' (the extraction target is always a dedicated
// DB) — the caller shows that server message plainly rather than pre-computing it.
export type ExtractionState =
  | 'requested' | 'preflight' | 'provisioning' | 'frozen' | 'copying' | 'verifying'
  | 'cutover' | 'done' | 'aborted' | 'rolled_back' | 'declined';
export interface BusinessExtraction {
  publicId: string;
  state: ExtractionState;
  sourceSchema: string;
  destSchema: string;
  error: string | null;
  requestedAt: string | null;
  frozenAt: string | null;
  cutoverAt: string | null;
  /** Set once the platform approves the request; the row stays 'requested' after
   *  approval — "is it approved?" is `approvedAt !== null`, never a state check.
   *  Approval is platform-only: this surface is read-only, no approve/decline here. */
  approvedAt: string | null;
  /** YYYY-MM-DD, the night the scheduler runs this extraction (fixed 23:30 Asia/Dhaka). */
  scheduledFor: string | null;
  /** Nights the scheduler's gate has deferred this row (spec §5). */
  deferralCount: number;
}
export interface BusinessExtractionStatus {
  current: BusinessExtraction | null;
  history: BusinessExtraction[];
}
const MOCK_EXTRACTION_STATUS: BusinessExtractionStatus = { current: null, history: [] };
export const getCustomerExtraction = (businessId: string) =>
  USE_MOCKS ? mockDelay(MOCK_EXTRACTION_STATUS) : apiGet<BusinessExtractionStatus>(`/reseller/customers/${businessId}/extraction`);
export const useCustomerExtraction = (businessId: string) =>
  useQuery({ queryKey: ['reseller', 'customer', businessId, 'extraction'], queryFn: () => getCustomerExtraction(businessId), enabled: !!businessId });
/** POST /reseller/customers/:id/extraction/request — creates the 'requested' row only;
 *  execution stays an operator-CLI action (see api/admin.ts's requestBusinessExtraction). */
export const requestCustomerExtraction = (businessId: string, body: { acknowledgement: string }) =>
  USE_MOCKS
    ? mockDelay<BusinessExtraction>({
        publicId: 'ext_mock', state: 'requested', sourceSchema: 'ogrogoti_master', destSchema: 'pending',
        error: null, requestedAt: new Date().toISOString(), frozenAt: null, cutoverAt: null,
        approvedAt: null, scheduledFor: null, deferralCount: 0,
      })
    : apiPost<BusinessExtraction>(`/reseller/customers/${businessId}/extraction/request`, body);

// ── Reseller vanity domains (CRUD) ───────────────────────────────────────────
export type ResellerDomain = {
  id: string;
  hostname: string;
  isPrimary: boolean;
  dnsStatus: 'pending' | 'verified' | 'failed';
  tlsStatus: 'pending' | 'issued' | 'failed';
};
const MOCK_DOMAINS: ResellerDomain[] = [
  { id: 'rd_1', hostname: 'pos.acme-resellers.com', isPrimary: true, dnsStatus: 'verified', tlsStatus: 'issued' },
];
export const listResellerDomains = () =>
  USE_MOCKS ? mockDelay(MOCK_DOMAINS) : apiGetList<ResellerDomain>('/reseller/domains');
export const useResellerDomains = () => useQuery({ queryKey: ['reseller', 'domains'], queryFn: listResellerDomains });
export const addResellerDomain = (host: string) =>
  USE_MOCKS
    ? mockDelay<ResellerDomain>({ id: `rd_${host}`, hostname: host, isPrimary: false, dnsStatus: 'pending', tlsStatus: 'pending' })
    : apiPost<ResellerDomain>('/reseller/domains', { host });
export const removeResellerDomain = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/reseller/domains/${id}`);

export type DomainVerifyResult = { id: string; host: string; dnsStatus: ResellerDomain['dnsStatus']; tlsStatus: ResellerDomain['tlsStatus']; verifiedAt: string | null };
/** Re-check DNS for one domain right now (manual trigger; the API also sweeps every 5 min). */
export const verifyResellerDomain = (id: string) =>
  USE_MOCKS
    ? mockDelay<DomainVerifyResult>({ id, host: '', dnsStatus: 'verified', tlsStatus: 'pending', verifiedAt: new Date().toISOString() })
    : apiPost<DomainVerifyResult>(`/reseller/domains/${id}/verify`, {});

// ── Reseller coupons (CRUD) ──────────────────────────────────────────────────
export type ResellerCoupon = {
  id: string;
  code: string;
  internalName?: string;
  discountType: 'percent' | 'fixed' | 'trial_extension' | 'free_intervals';
  amountLabel: string;
  redemptionsUsed: number;
  redemptionsLimit: number | null;
  expiresAt: string | null;
  status: 'active' | 'expired' | 'draft' | 'disabled';
  resellerScoped: boolean;
};
const MOCK_COUPONS: ResellerCoupon[] = [
  { id: 'rc_1', code: 'ACME-EID2026', discountType: 'percent', amountLabel: '20% off', redemptionsUsed: 12, redemptionsLimit: 100, expiresAt: null, status: 'active', resellerScoped: true },
];
export const listResellerCoupons = () =>
  USE_MOCKS ? mockDelay(MOCK_COUPONS) : apiGetList<ResellerCoupon>('/reseller/coupons');
export const useResellerCoupons = () => useQuery({ queryKey: ['reseller', 'coupons'], queryFn: listResellerCoupons });
/**
 * Cancel (or resume) a customer's subscription at period end (#5). Reseller-scoped:
 * the server resolves the business under the reseller's RLS, so a foreign customer
 * 404s rather than being cancellable.
 */
export const cancelCustomerSubscription = (
  businessId: string,
  input: { resume?: boolean; reason?: string } = {},
) =>
  USE_MOCKS
    ? mockDelay({ ok: true, cancelAtPeriodEnd: input.resume !== true, currentPeriodEnd: null })
    : apiPost<{ ok: boolean; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }>(
        `/reseller/customers/${businessId}/subscription/cancel`,
        input,
      );

export const addResellerCoupon = (input: { code: string; discountType: string; discountValue: number; maxRedemptions?: number | null; expiresAt?: string | null }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost('/reseller/coupons', input);
export const removeResellerCoupon = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/reseller/coupons/${id}`);

// ── Reseller packages + pricing tiers (CRUD) ─────────────────────────────────
export const listResellerPackages = () =>
  USE_MOCKS ? mockDelay(resellerPackages.rsl_1 ?? []) : apiGetList<ResellerPackage>('/reseller/packages');
export const useResellerPackages = () =>
  useQuery({ queryKey: ['reseller', 'packages'], queryFn: listResellerPackages });

export const listResellerPricingTiers = () =>
  USE_MOCKS ? mockDelay(platformPricingTiers) : apiGetList<PlatformPricingTier>('/reseller/pricing-tiers');
export const useResellerPricingTiers = () =>
  useQuery({ queryKey: ['reseller', 'pricingTiers'], queryFn: listResellerPricingTiers });

export type NewResellerPackageInput = {
  name: string;
  internalCode?: string;
  tagline?: string;
  tierSlug: string;
  monthlyMinor: number;
  yearlyMinor: number;
  status: 'draft' | 'published';
  features: {
    locations: number | 'unlimited';
    users: number | 'unlimited';
    products: number | 'unlimited';
    invoices: number | 'unlimited';
    modules: string[];
  };
};
export const addResellerPackage = (input: NewResellerPackageInput) =>
  USE_MOCKS ? mockDelay({ id: `rpk_new`, name: input.name, ok: true }) : apiPost('/reseller/packages', input);
export const removeResellerPackage = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/reseller/packages/${id}`);
export const updateResellerPackage = (
  id: string,
  patch: {
    name?: string;
    tagline?: string;
    tierSlug?: string;
    monthlyMinor?: number;
    yearlyMinor?: number;
    status?: 'draft' | 'published' | 'deprecated' | 'archived';
    features?: NewResellerPackageInput['features'];
  },
) => (USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/reseller/packages/${id}`, patch));

// ── Reseller add-ons (own catalog extras — sold only to this reseller's customers) ──
export type ResellerAddOn = {
  id: string;
  internalCode: string;
  name: string;
  description: string;
  unitLabel: string;
  amount: number;
  currency: string;
  billing: 'recurring' | 'one_time';
  status: string;
  inUseCount: number;
  compatiblePackageIds: string[];
  conditions: Record<string, number> | null;
};
export type ResellerAddOnInput = {
  name: string;
  description?: string;
  billing: 'recurring' | 'one_time';
  priceMinor: number;
  status?: 'active' | 'draft' | 'archived';
  packageIds?: string[];
  conditions?: Record<string, number> | null;
};
export const listResellerAddOns = () =>
  USE_MOCKS ? mockDelay([] as ResellerAddOn[]) : apiGetList<ResellerAddOn>('/reseller/add-ons');
export const useResellerAddOns = () =>
  useQuery({ queryKey: ['reseller', 'addons'], queryFn: listResellerAddOns });
export const addResellerAddOn = (input: ResellerAddOnInput) =>
  USE_MOCKS ? mockDelay({ id: 'rao_new', name: input.name }) : apiPost('/reseller/add-ons', input);
export const updateResellerAddOn = (id: string, patch: Partial<ResellerAddOnInput>) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/reseller/add-ons/${id}`, patch);

// ── Reseller staff invites (link + email) ────────────────────────────────────
export type ResellerInviteResult = { ok: boolean; inviteUrl: string; email: string; expiresAt?: string };
/** Result of `action: 'create'` — the account exists immediately (no invite link). */
export type ResellerCreateUserResult = { ok: boolean; action: 'create'; userId: number; email: string; created: boolean; passwordSet: boolean; posRole?: string };
/**
 * Add a reseller staff member — POST /reseller/staff. `action: 'invite'` (default)
 * emails a secure accept link; `action: 'create'` sets a password now (min 8) so they
 * can sign in immediately.
 */
export const inviteResellerStaff = (
  body: { action?: 'create' | 'invite'; email: string; fullName?: string; role: 'admin' | 'staff' | 'readonly'; password?: string },
): Promise<ResellerInviteResult | ResellerCreateUserResult> =>
  USE_MOCKS
    ? body.action === 'create'
      ? mockDelay<ResellerCreateUserResult>({ ok: true, action: 'create', userId: 1, email: body.email, created: true, passwordSet: true })
      : mockDelay<ResellerInviteResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: body.email })
    : apiPost<ResellerInviteResult | ResellerCreateUserResult>('/reseller/staff', body);
/**
 * Add a user (owner/staff) to one of this reseller's CUSTOMER businesses —
 * POST /reseller/customers/:businessPublicId/users. `posRole` maps to the POS
 * role (Admin/Manager/Cashier). Invite emails a link; create sets a password now.
 */
export const createResellerCustomerUser = (
  businessPublicId: string,
  body: { action?: 'create' | 'invite'; email: string; name?: string; posRole?: string; password?: string; username?: string },
): Promise<ResellerInviteResult | ResellerCreateUserResult> =>
  USE_MOCKS
    ? body.action === 'create'
      ? mockDelay<ResellerCreateUserResult>({ ok: true, action: 'create', userId: 1, email: body.email, created: true, passwordSet: true, posRole: body.posRole })
      : mockDelay<ResellerInviteResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: body.email })
    : apiPost<ResellerInviteResult | ResellerCreateUserResult>(`/reseller/customers/${businessPublicId}/users`, body);
/** Resend a pending staff invite (inviteId is the `inv_`-prefixed row id from the list). */
export const resendResellerStaffInvite = (inviteId: string) =>
  USE_MOCKS
    ? mockDelay<ResellerInviteResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: '' })
    : apiPost<ResellerInviteResult>(`/reseller/staff/${inviteId}/resend`);

export const useCurrentReseller = () =>
  useQuery({ queryKey: ['reseller', 'me'], queryFn: getCurrentReseller });
export const useResellerCustomers = () =>
  useQuery({ queryKey: ['reseller', 'customers'], queryFn: listResellerCustomers });
export const useResellerStaff = () =>
  useQuery({ queryKey: ['reseller', 'staff'], queryFn: listResellerStaff });
export const useResellerRevenue = () =>
  useQuery({ queryKey: ['reseller', 'revenue'], queryFn: getResellerRevenue });
export const useResellerKpis = () =>
  useQuery({ queryKey: ['reseller', 'kpis'], queryFn: getResellerKpis });
export const useResellerActivity = () =>
  useQuery({ queryKey: ['reseller', 'activity'], queryFn: listResellerActivity });
export const useApplicantContext = () =>
  useQuery({ queryKey: ['reseller', 'applicantContext'], queryFn: getApplicantContext });
export const useResellerPayments = () =>
  useQuery({ queryKey: ['reseller', 'payments'], queryFn: listResellerPayments });
